import { Entity } from "../../Entity";
import ChargeFactory from "../../Factories/ChargeFactory";
import Translator from "../../../Translation/Translator";
import Formatter from "../../../Localization/Formatter";
import { FactoryInterface } from "../../FactoryInterface";
import { CompanyInterface } from "../Company/CompanyInterface";
import { ChargeInterface } from "./ChargeInterface";
import { PersonInterface } from "../Person/PersonInterface";
import { UnitInterface } from "../Unit/UnitInterface";
import { PropertyInterface } from "../Property/PropertyInterface";
import { ContractInterface } from "../Contract/ContractInterface";
import { EventInterface } from "../Event/EventInterface";
import { ChargeClassInterface } from "./ChargeClass/ChargeClassInterface";
import { CurrencyInterface } from "../../../Currency/CurrencyInterface";
import { ChargeTemplateInterface } from "../Charge/ChargeTemplateInterface";
import { TypeInterface } from "./Type/TypeInterface";
import { PeriodInterface } from "./Period/PeriodInterface";
import { AttachmentInterface } from "../Attachment/AttachmentInterface";
import TypeContainer from "./Type/TypeContainer";
import Store from "../../../Store/Store";

class Charge extends Entity implements ChargeInterface {

  constructor(public data) {
    super();
  }

  public getFactory(): FactoryInterface {
    return ChargeFactory;
  }

  get nameOfEntity() {
    return Translator.trans("charge");
  }

  get nameForUrl() {
    return "charge";
  }

  get labelForEntity() {
    return "charge"
  }

  get id(): number {
    return this.data.id;
  }

  get linkedChargeId(): number {
    return this.data.linkedChargeId;
  }

  get linkedCharge(): ChargeInterface {
    return this.data.linkedCharge;
  }

  get amount(): number {
    return this.data.amount;
  }

  get amountPaid(): number {
    return this.data.amountPaid;
  }

  get amountPaidInCurrentPeriod(): number {
    return this.data.amountPaidInCurrentPeriod;
  }

  get amountRemaining(): number {
    return this.amount - this.amountPaid;
  }

  get companyId(): number {
    return this.data.companyId;
  }

  get company(): CompanyInterface {
    return [...Store.getConfigurator().getState().app.companies]
            .find(company => company.id === this.companyId);
  }

  get owner(): CompanyInterface {
    return [...Store.getConfigurator().getState().app.companies]
            .find(company => company.id === this.ownerId);
  }

  get contactId(): number {
    return this.data.contactId;
  }

  get contact(): PersonInterface {
    return this.relation("app.persons", this.data.contactId)[0];
  }

  get counterparty(): CompanyInterface | PersonInterface {
    if (!!this.contact) {
      return this.contact;
    }

    if (!!this.company) {
      return this.company;
    }
  }

  get payer(): CompanyInterface | PersonInterface {
    if (!!this.contact) {
      return this.contact;
    }

    if (!!this.company) {
      return this.company;
    }

    if (!!this.property) {
      return this.property.contact || this.property.company;
    }

    if (!!this.unit) {
      return this.unit.property.contact || this.unit.property.company;
    }

    if (!!this.contract) {
      return this.contract.occupant;
    }
  }

  get contractId(): number {
    return this.data.contractId;
  }

  get contract(): ContractInterface {
    return this.relation("app.contracts", this.data.contractId)[0];
  }

  get unitId(): number {
    return this.data.unitId;
  }

  get unit(): UnitInterface {
    return this.newRelation("units.byId", this.data.unitId)[0];
  }

  get propertyId(): number {
    return this.data.propertyId;
  }

  get ownerId(): number {
    return this.data.ownerId;
  }

  get property(): PropertyInterface {
    return this.newRelation("properties.byId", this.data.propertyId)[0];
  }

  get eventId(): number {
    return this.data.eventId;
  }

  get event(): EventInterface {
    return this.data.event;
  }

  get relatedEntity():  ContractInterface | UnitInterface | PropertyInterface | CompanyInterface | EventInterface {
    if (this.propertyId) {
      return this.property;
    }

    if (this.unitId) {
      return this.unit;
    }

    if (this.contractId) {
      return this.contract;
    }

    if (this.ownerId) {
      return this.owner;
    }

    if (this.eventId) {
      return this.event;
    }
  }

  get description(): string {
    if (!this.data.description) {
      return "";
    }

    return this.data.description;
  }

  get dueDate(): Date {
    return this.data.dueDate;
  }

  get chargeClass(): ChargeClassInterface {
    return this.data.chargeClass;
  }

  get type(): TypeInterface {
    return this.data.type;
  }

  get chargeTemplateId(): number | undefined {
    return this.data.chargeTemplateId;
  }

  get chargeTemplates(): any[] {
    return this.data.chargeTemplates;
  }

  get chargeTemplate(): ChargeTemplateInterface | undefined {
    return this.relation("app.chargeTemplates", this.data.chargeTemplateId)[0];
  }

  get paymentDetails(): any {
    return this.data.paymentDetails;
  }

  get paymentIds(): number[] {
    return this.data.paymentIds.map(key => parseInt(key, 10));
  }

  get displayName(): string {
    const layers: string[] = [];

    this.payer && layers.push(this.payer?.displayName.trim());

    if (this.chargeTemplate) {
      layers.push(Formatter.checkTranslationType(Translator.trans(this.chargeTemplate?.type?.label, {}, "", this.chargeTemplate?.type?.system), this.chargeTemplate?.type?.label, this.chargeTemplate?.type?.system));
      layers.push(Formatter.formatNumber(this.amount, this.currency));
      layers.push(this.chargeTemplate.repeatPeriod.label);
      // layers.push(Formatter.simpleFormatNumber(this.amount));
    } else {
      layers.push(Formatter.formatNumber(this.amount, this.currency));
      // layers.push(Formatter.simpleFormatNumber(this.amount));
      layers.push(this.description || "(" + Translator.trans("title-manual") + ")");
    }

    return layers.filter(layer => !!layer).join(", ");
  }

  get listDisplayName(): string {
    return this.nameOfEntity + ", " + this.displayName;
  }

  get currency(): CurrencyInterface {
    return this.data.currency;
  }
  get currencyId(): number {
    return this.data.currencyId;
  }

  get automaticallyCreated(): boolean {
    return this.data.automaticallyCreated;
  }

  get creationTypeDisplayName(): string {
    if (!this.automaticallyCreated) {
      return Translator.trans("creation-type-manual");
    }

    return "";
  }

  get createdAt(): Date {
    return this.data.createdAt;
  }

  get removed(): boolean {
    return this.data.removed;
  }

  get isLocked(): boolean {
    return this.data.isLocked;
  }

  get period(): PeriodInterface {
    return this.data.period;
  }

  // firstDate - contains the "dateOfStatementPeriod" (indicates the statement in which payment will be included)
  get firstDate(): Date {
    return this.data.firstDate;
  }

  // dateOfPeriod - contains the period for which charge is created
  get dateOfPeriod(): Date {
    return this.data.dateOfPeriod;
  }

  get oldestPaymentDate(): Date {
    return this.data.oldestPaymentDate;
  }

  get lastDate(): Date {
    return this.data.lastDate;
  }

  get lastDateOfPeriod(): Date {
    return this.data.lastDateOfPeriod;
  }

  get isSettle(): boolean {
    return this.data.isSettle;
  }

  get invoiceDate(): Date {
    return this.data.invoiceDate;
  }

  get orderDate(): Date {
    return this.data.orderDate;
  }

  get invoiceNumber(): string {
    return this.data.invoiceNumber;
  }

  get vat(): number {
    return this.data.vat;
  }

  get vatPercantage(): number {
    return Math.round(this.vat / (this.amount - this.vat) * 100);
  }

  get isInvoice(): boolean {
    return this.data.isInvoice;
  }

  get amazonFolderName(): string {
    return this.data.amazonFolderName;
  }

  get attachments(): AttachmentInterface[] {
    return this.data.attachments;
  }

  get ownerFinancialStatementId(): number {
    return this.data.ownerFinancialStatementId;
  }

  get agencyId(): number {
    return this.data.agencyId;
  }

  get agencyFeeChargeId(): number {
    return this.data.agencyFeeChargeId;
  }

  get agencyFinancialStatementId(): number {
    return this.data.agencyFinancialStatementId;
  }

  get chargeClassId(): number {
    return this.data.chargeClassId;
  }

  get createdBy(): number {
    return this.data.createdBy;
  }

  get updatedAt(): Date {
    return this.data.updatedAt;
  }

  get updatedBy(): number {
    return this.data.updatedBy;
  }

  get automaticallyRemoved(): boolean {
    return this.data.automaticallyRemoved;
  }

  get amountPaidToOwner(): number {
    return this.data.amountPaidToOwner;
  }

  get utilityDetails(): any {
    return this.data.utilityDetails;
  }

  get relatedTenantCharges(): number[] {
    return this.data.relatedTenantCharges || [];
  }

  get collectiveChargeTemplateIds(): number[] {
    return this.data.collectiveChargeTemplateIds || [];
  }

  get previousObjectContractId(): number {
    return this.data.previousObjectContractId;
  }

  get delinquentDay(): number {
    return this.data.delinquentDay;
  }

  get ledgerAccountId(): number {
    return this.data.ledgerAccountId;
  }

  get invoiceReference(): number {
    return this.data.invoiceReference;
  }

  get fullName(): string {
    return this.data.fullName;
  }

  get isMigrationForInvoiceDate(): boolean {
    return this.data.isMigrationForInvoiceDate;
  }

  get snoozedByUser(): boolean {
    return this.data.snoozedByUser;
  }

  get isBill(): boolean {
    return this.data.isBill;
  }

  get isToBeAnInvoice(): boolean {
    return this.data.isToBeAnInvoice;
  }

  get isCounterpartyAgency(): boolean {
    return this.data.isCounterpartyAgency;
  }

  get isCounterpartyOwner(): boolean {
    return this.data.isCounterpartyOwner;
  }

  get isCreatedToSettleOnePaymentWithSavings(): boolean {
    return this.data.isCreatedToSettleOnePaymentWithSavings;
  }

  get isCreatedToSettleTwoPayments(): boolean {
    return this.data.isCreatedToSettleTwoPayments;
  }

  get isDeposit(): boolean {
    return this.data.isDeposit;
  }

  get isPayout(): boolean {
    return this.data.isPayout;
  }

  get isRefund(): boolean {
    return this.data.isRefund;
  }

  get isSettlement(): boolean {
    return this.data.isSettlement;
  }

  get isUnpaid(): boolean {
    return this.data.isUnpaid;
  }

  get isWithdrawal(): boolean {
    return this.data.isWithdrawal;
  }

  get isSavings(): boolean {
    return this.data.isSavings;
  }

  get isObjectOwner(): boolean {
    return this.data.isObjectOwner;
  }

  get hasOwnerAsObject(): boolean {
    return this.data.hasOwnerAsObject;
  }

  get hasAgencyAsObject(): boolean {
    return this.data.hasAgencyAsObject;
  }

  get financialStatement(): any {
    return this.data.financialStatement;
  }

  get note(): string {
    return this.data.note;
  }

  get collectiveVat(): any[] {
    return this.data.collectiveVat;
  }

  get isReceivable(): boolean {
    return this.data.isReceivable;
  }

  get isPayable(): boolean {
    return this.data.isPayable;
  }

  get isCredit(): boolean {
    return this.data.isCredit;
  }

  get hasPayoutType(): boolean {
    return this.data.hasPayoutType;
  }

  get hasSettlementType(): boolean {
    return this.data.hasSettlementType;
  }

  get hasSavingsType(): boolean {
    return this.data.hasSavingsType;
  }

  get hasRefundType(): boolean {
    return this.data.hasRefundType;
  }

  get isCreditType(): boolean {
    return this.data.isCreditType;
  }

  get isCredited(): boolean {
    return this.data.isCredited;
  }

  get hasSavingsWithdrawalType(): boolean {
    return this.type?.id === TypeContainer.pickIdFromLabel("Savings_account_withdraw");
  }

  public isFullyPaid(): boolean {
    return this.data.isFullyPaid;
  }

  public getIconColor(): string {
    if (this.isFullyPaid()) {
      return "#43A047";
    }

    if (this.amountPaid === 0) {
      return "#F44336";
    }

    return "#F5A623";
  }

  public getChargePaidAmountColorForStatement(): string {
    if (this.isPaidDirectlyToOwner) {
      return "initial";
    }

    if (this.amount !== 0 && this.amountPaidInCurrentPeriod === 0 && this.paymentIds.length === 0) {
      return "#F44336";
    }

    if (this.amountPaidInCurrentPeriod === 0 && this.paymentIds.length > 0 && this.isFullyPaid()) {
      return "initial";
    }

    if (this.amountPaidInCurrentPeriod !== 0 && this.isFullyPaid()) {
      return "initial";
    }

    if (this.amount === this.amountPaidInCurrentPeriod) {
      return "initial";
    }

    if (this.amount === 0) {
      return "initial";
    }

    return "#F5A623";
  }

  public getStatusText(): string {
    if (this.amountPaid === 0) {
      return Translator.trans("status-unpaid");
    }

    if (this.isFullyPaid() === true) {
      return Translator.trans("status-paid");
    }

    return Translator.trans("status-partially-paid");
  }

  public amountPaidFromPayments(paymentIds: number[]): number {
    const linkedPayments = this.paymentIds.filter(paymentId => paymentIds.includes(paymentId));
    let sum = 0;

    linkedPayments.forEach(paymentId => {
      sum += this.paymentDetails[paymentId];
    });

    return sum;
  }

  get isReferencedToSavings(): boolean {
    return this.data.isReferencedToSavings;
  }

  get isPartOfASettlementWithSavings(): boolean {
    return this.data.isPartOfASettlementWithSavings;
  }

  get isSnoozed(): boolean {
    return this.data.isSnoozed;
  }

  get snoozeDate (): Date {
    return this.data.snoozeDate;
  }

  get linkedPayments() {
    return this.data.linkedPayments;
  }

  get relatedPayments() {
    return this.data.relatedPayments;
  }

  get payments() {
    return this.data.payments;
  }

  get hasMemos(): boolean {
    return this.data.hasMemos;
  }

  get isRemoved(): boolean {
    return this.data.isRemoved;
  }
  
  get isSent(): boolean {
    return this.data.isSent;
  }

  get isPaidDirectlyToOwner(): boolean {
    return this.data.isPaidDirectlyToOwner;
  }

  get creditCharges(): ChargeInterface[] {
    return this.data.creditCharges;
  }

  get isReadonly(): boolean {
    return this.data.isReadOnly;
    // return this.hasPayoutType ||
    //        this.hasSavingsType ||
    //        this.isReferencedToSavings ||
    //        this.hasSettlementType ||
    //        this.isSettle ||
    //        this.isCredit ||
    //        this.isLocked ||
    //        this.isPaidDirectlyToOwner ||
    //        this.hasRefundType ||
    //        this.isLocked ||
    //        this.removed;
  }

  get name(): string {
    return this.data.name;
  }
  
  get automaticallyLinked(): boolean {
    return this.data.automaticallyLinked;
  }
  
  get settledCharges(): any[] {
    return this.data.settledCharges;
  }
  
  get settledChargeIds(): number[] {
    return this.data.settledChargeIds;
  }

  get isPaid(): boolean {
    return this.data.isPaid;
  }

  get isPartiallyPaid(): boolean {
    return this.data.isPartiallyPaid;
  }
}

export { Charge }
